import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import { todayIso } from "../docs/helpers";
import type { OpDay, OpList, ReminderInput, ReminderKind, ReminderView } from "../types";
import { useMessage } from "./Message";
import { isOperatingList } from "./op-move";
import { MONTHS, dayShort, defaultDayList, reminderIn, reminderWhen } from "./reminders";

/** «2026-11» + n месяцев → «2027-01». */
function addMonths(ym: string, n: number): string {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** «2026-10-31» + n месяцев → «2026-11-30»: число не вылезает за конец месяца. */
function addMonthsDate(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const last = new Date(y, m - 1 + n + 1, 0).getDate();
  const t = new Date(y, m - 1 + n, Math.min(d, last));
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
}

/** «2026-10-24» + n дней → «2026-10-31». */
function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const t = new Date(y, m - 1, d + n);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
}

/**
 * Поставить или изменить напоминание строке оперсписка.
 *
 * Главный случай — «хочет оперироваться в марте»: месяц выбирают плиткой
 * (второй щелчок по другому месяцу — диапазон «март — апрель»), и напоминание
 * сработает, как только в этих месяцах откроют операционный день. Плитки, где
 * дни уже открыты, помечены числом — видно, что напоминание сработает сразу и
 * пациента можно записывать. Второй вид — просто дата: «перезвонить 15-го».
 *
 * inline — внутри карточки напоминания, без своего окна.
 */
export default function ReminderEditor({
  entry,
  reminder,
  inline = false,
  onSaved,
  onCancel,
  onDeleted,
}: {
  entry: { entry_id: number; pat_fio: string; list_id: number; oper_type: string };
  /** Правка существующего; null — новое. */
  reminder: ReminderView | null;
  inline?: boolean;
  onSaved: (reminder: ReminderView) => void;
  onCancel: () => void;
  /** «Снять напоминание» — только у существующего. */
  onDeleted?: () => void;
}) {
  const message = useMessage();
  const today = todayIso();
  const thisMonth = today.slice(0, 7);
  const [lists, setLists] = useState<OpList[] | null>(null);
  const [kind, setKind] = useState<ReminderKind>(reminder?.kind ?? "month");
  // Новое напоминание — без месяца: его выбирают явно, а не соглашаются с подставленным.
  const [from, setFrom] = useState(reminder?.want_from?.slice(0, 7) ?? "");
  const [to, setTo] = useState(reminder?.want_to?.slice(0, 7) ?? reminder?.want_from?.slice(0, 7) ?? "");
  /** Месяц, выбранный последним щелчком: щелчок по другому сразу после — диапазон. */
  const [anchor, setAnchor] = useState<string | null>(null);
  const [listId, setListId] = useState<number | null>(reminder?.day_list_id ?? null);
  const [on, setOn] = useState(reminder?.remind_on ?? addDays(today, 7));
  const [note, setNote] = useState(reminder?.note ?? "");
  /** Какие 12 месяцев на плитках: 0 — с текущего, 1 — следующие двенадцать. */
  const [page, setPage] = useState(() => {
    const start = reminder?.want_from?.slice(0, 7);
    if (!start) return 0;
    const [sy, sm] = start.split("-").map(Number);
    const [ty, tm] = thisMonth.split("-").map(Number);
    return Math.max(0, Math.floor(((sy - ty) * 12 + (sm - tm)) / 12));
  });
  const [days, setDays] = useState<OpDay[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .opLists()
      .then((r) => {
        setLists(r.lists);
        setListId((cur) => cur ?? defaultDayList(r.lists, entry)?.list_id ?? null);
      })
      .catch((err) => message(err as Error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Открытые предстоящие дни списка — чтобы пометить месяцы, где они уже есть.
  useEffect(() => {
    if (!listId) return;
    let alive = true;
    api
      .opDays(listId)
      .then((r) => alive && setDays(r.days))
      .catch(() => alive && setDays([]));
    return () => {
      alive = false;
    };
  }, [listId]);

  const operating = useMemo(() => (lists ?? []).filter(isOperatingList), [lists]);
  const months = Array.from({ length: 12 }, (_, i) => addMonths(thisMonth, page * 12 + i));
  const daysIn = (ym: string) => (days ?? []).filter((d) => d.op_date.slice(0, 7) === ym);
  const inRange = (days ?? []).filter((d) => {
    const m = d.op_date.slice(0, 7);
    return m >= from && m <= to;
  });

  /** Щелчок — один месяц; щелчок по другому сразу после — диапазон между ними; дальше снова один. */
  function pickMonth(ym: string) {
    if (anchor && anchor !== ym) {
      setFrom(ym < anchor ? ym : anchor);
      setTo(ym < anchor ? anchor : ym);
      setAnchor(null);
    } else {
      setFrom(ym);
      setTo(ym);
      setAnchor(ym);
    }
  }

  const when = { kind, want_from: `${from}-01`, want_to: `${to}-01`, remind_on: on };

  const ready = kind === "month" ? !!from : !!on;

  async function save() {
    if (!ready) return;
    const body: ReminderInput =
      kind === "month"
        ? { kind, want_from: from, want_to: to, day_list_id: listId, note }
        : { kind, remind_on: on, note };
    setBusy(true);
    try {
      const res = reminder
        ? await api.updateReminder(reminder.rem_id, body)
        : await api.createReminder(entry.entry_id, body);
      message(
        reminder
          ? `Напоминание изменено: ${reminderWhen(res.reminder)}`
          : `Напоминание поставлено: ${reminderWhen(res.reminder)}`
      );
      onSaved(res.reminder);
    } catch (err) {
      message(err as Error);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!reminder) return;
    setBusy(true);
    try {
      await api.deleteReminder(reminder.rem_id);
      message("Напоминание снято");
      onDeleted?.();
    } catch (err) {
      message(err as Error);
      setBusy(false);
    }
  }

  const body = (
    <div className="rem-editor">
      <div className="rem-kind" role="radiogroup">
        <span
          role="radio"
          aria-checked={kind === "month"}
          className={kind === "month" ? "on" : undefined}
          onClick={() => setKind("month")}
        >
          Когда откроют опердень
        </span>
        <span
          role="radio"
          aria-checked={kind === "date"}
          className={kind === "date" ? "on" : undefined}
          onClick={() => setKind("date")}
        >
          В дату
        </span>
      </div>

      {kind === "month" ? (
        <>
          <div className="rem-label">
            Хочет оперироваться
            <span className="rem-label-hint">число на месяце — сколько опердней уже открыто</span>
          </div>
          <div className="rem-months-nav">
            <i
              className={`opw-cal-prev${page ? "" : " disabled"}`}
              title="Раньше"
              onClick={() => page && setPage(page - 1)}
            />
            <div className="rem-months">
              {months.map((ym) => {
                const [y, m] = ym.split("-").map(Number);
                const n = daysIn(ym).length;
                const picked = ym >= from && ym <= to;
                return (
                  <div
                    key={ym}
                    className={[
                      "rem-month",
                      picked ? "on" : null,
                      ym === from || ym === to ? "edge" : null,
                      n ? "has-days" : null,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    title={n ? `Открыто операционных дней: ${n}` : "Операционных дней пока нет"}
                    onClick={() => pickMonth(ym)}
                  >
                    <b>{MONTHS[m - 1]}</b>
                    <span>{y}</span>
                    {n > 0 && <i>{n}</i>}
                  </div>
                );
              })}
            </div>
            <i className="opw-cal-next" title="Позже" onClick={() => setPage(page + 1)} />
          </div>

          {operating.length > 1 && (
            <label className="iol-dlg-field rem-field">
              <span>Ждать дни списка</span>
              <select value={listId ?? ""} onChange={(e) => setListId(Number(e.target.value) || null)}>
                {operating.map((l) => (
                  <option key={l.list_id} value={l.list_id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className={`rem-preview${from && inRange.length ? " rem-preview-now" : ""}`}>
            {!from
              ? "Выберите месяц. Щелчок по другому месяцу сразу после — несколько месяцев подряд."
              : days === null
              ? "Смотрим открытые дни…"
              : inRange.length
                ? `Уже ${inRange.length === 1 ? "открыт день" : `открыто дней: ${inRange.length}`} ${reminderIn(when)}: ${inRange
                    .slice(0, 4)
                    .map((d) => dayShort(d.label).split(",")[0])
                    .join(", ")}${inRange.length > 4 ? "…" : ""} — напоминание сработает сразу.`
                : `Дней ${reminderIn(when)} пока нет — напомним, как только откроют.`}
          </div>
        </>
      ) : (
        <>
          <label className="iol-dlg-field rem-field">
            <span>Напомнить</span>
            <input type="date" value={on} min={today} onChange={(e) => setOn(e.target.value)} />
          </label>
          <div className="rem-quick">
            {[
              ["через неделю", addDays(today, 7)],
              ["через 2 недели", addDays(today, 14)],
              ["через месяц", addMonthsDate(today, 1)],
              ["через 3 месяца", addMonthsDate(today, 3)],
            ].map(([label, date]) => (
              <span key={label} className={on === date ? "on" : undefined} onClick={() => setOn(date)}>
                {label}
              </span>
            ))}
          </div>
        </>
      )}

      <label className="iol-dlg-field rem-field">
        <span>Комментарий</span>
        <input
          type="text"
          value={note}
          maxLength={250}
          placeholder="Например: хочет после отпуска, звонить после 17:00"
          onChange={(e) => setNote(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !busy && ready && save()}
        />
      </label>

      <div className="iol-dlg-buttons rem-editor-buttons">
        <div
          className={`button green${busy || !ready ? " disabled" : ""}`}
          title={ready ? undefined : "Сначала выберите месяц"}
          onClick={busy || !ready ? undefined : save}
        >
          {reminder ? "Сохранить" : "Поставить напоминание"}
        </div>
        <div className="button" onClick={onCancel}>
          Отмена
        </div>
        {reminder && onDeleted && (
          <u className="hist-link rem-remove" onClick={busy ? undefined : remove}>
            снять напоминание
          </u>
        )}
      </div>
    </div>
  );

  if (inline) return body;
  // Порталом: из прилипшей шапки оперсписка окно иначе считалось бы от неё.
  return createPortal(
    <div className="iol-dlg-overlay overlay" onClick={onCancel}>
      <div className="iol-dlg rem-dlg" onClick={(e) => e.stopPropagation()}>
        <div className="iol-dlg-title">
          Напоминание
          <span className="rem-dlg-who">{entry.pat_fio}</span>
        </div>
        {body}
      </div>
    </div>,
    document.body
  );
}
